import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { useState } from 'react'

import type { Language } from '@/bindings/Language'
import type { PlacedOrderLine } from '@/bindings/PlacedOrderLine'
import type { QueueOrder } from '@/bindings/QueueOrder'
import { Button } from '@/components/button'
import { Input } from '@/components/field'
import { FormError } from '@/components/form-error'
import { PrintIcon } from '@/components/icons'
import { Card, Page } from '@/components/layout'
import { auditKeys, orderHistoryQuery } from '@/features/audit-log/api'
import { AuditEntryRow } from '@/features/audit-log/entry-row'
import { backOfficeKeys, orderQuery, returnLine, setRealWeight } from '@/features/back-office/api'
import { BackLink } from '@/features/back-office/back-link'
import { OrderActions } from '@/features/back-office/order-actions'
import { ACTIVE_STATUSES } from '@/features/back-office/queue-filter'
import { remainingSteps } from '@/features/ordering/order-rules'
import { LineNote, LiveIndicator, OrderCard } from '@/features/ordering/order-view'
import { claim } from '@/lib/api'
import { cx } from '@/lib/cx'
import { useLocalizedText } from '@/lib/i18n/localized-text'
import { useLanguage } from '@/lib/i18n/store'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { branchEventsUrl, printPage, useBranchEvents } from '@/lib/live'
import { formatQuantity } from '@/lib/menu-format'
import { fromSteps, parseSteps, toSteps } from '@/lib/quantity'

const ROUTE_ID = '/$restaurantSlug/$branchSlug/manage/orders/$orderId'
const QUEUE_PATH = '/$restaurantSlug/$branchSlug/manage/queue'

interface Scope {
  restaurantId: string
  branchId: string
  orderId: string
}

type Tab = 'return' | 'weight'

/**
 * One order for staff (PRD, G10): the ticket in full, the queue's moves, a print button
 * (an 80 mm thermal ticket, G14), and the two T16 edits behind "Edit" — returns on any
 * order, real weight on by-weight lines while the order is active. Every answer is the
 * server's recomputed ticket (T8); live through G5's hints, polling behind them.
 */
export function OrderDetailsPage() {
  const t = useT()
  const { restaurantSlug, branchSlug, restaurantId, branchId, context } = useManagedBranch()
  const { orderId } = useParams({ from: ROUTE_ID })
  const queryClient = useQueryClient()
  const order = useQuery(orderQuery(restaurantId, branchId, orderId))
  const [editing, setEditing] = useState(false)
  const branch = context?.branches.find((candidate) => candidate.id === branchId)
  const live = useBranchEvents(context && branch ? branchEventsUrl(context.settings.slug, branch.slug) : undefined, () => {
    void queryClient.invalidateQueries({ queryKey: backOfficeKeys.order(restaurantId, branchId, orderId) })
    void queryClient.invalidateQueries({ queryKey: auditKeys.orderHistory(restaurantId, branchId, orderId) })
  })

  const scope: Scope = { restaurantId, branchId, orderId }
  const back = <BackLink to={QUEUE_PATH} params={{ restaurantSlug, branchSlug }} />
  if (order.isPending) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {back}
        <p className="text-muted text-sm">{t('common.loading')}</p>
      </Page>
    )
  }
  if (!order.data) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {back}
        {claim(order.error, 'NOT_FOUND') ? (
          <p role="alert" className="text-muted text-sm">
            {t('orderDetails.notFound')}
          </p>
        ) : (
          <>
            <FormError error={order.error} />
            <Button variant="secondary" onClick={() => void order.refetch()} className="self-start">
              {t('common.retry')}
            </Button>
          </>
        )}
      </Page>
    )
  }

  // The server's answer replaces the order at once (T8); without the stream, the queue is refetched behind it.
  function take(updated: QueueOrder) {
    queryClient.setQueryData(backOfficeKeys.order(restaurantId, branchId, orderId), updated)
    void queryClient.invalidateQueries({ queryKey: auditKeys.orderHistory(restaurantId, branchId, orderId) })
    if (live !== 'live') void queryClient.invalidateQueries({ queryKey: backOfficeKeys.queue(restaurantId, branchId) })
  }
  function stale() {
    void queryClient.invalidateQueries({ queryKey: backOfficeKeys.order(restaurantId, branchId, orderId) })
    void queryClient.invalidateQueries({ queryKey: backOfficeKeys.queue(restaurantId, branchId) })
  }

  return (
    <Page width="md" className="flex flex-col gap-4">
      {back}
      <OrderCard
        order={order.data}
        currency={order.data.currency}
        main={context.settings.main_language}
        heading="h1"
        code
        venue={{ restaurant: context.settings.name, branch: branch?.name, address: branch?.address, phone: branch?.phone }}
        actions={
          <OrderActions
            restaurantId={restaurantId}
            branchId={branchId}
            order={order.data}
            onMoved={take}
            onStale={stale}
            middle={
              <span className="flex items-center gap-2">
                <Button size="sm" variant="secondary" onClick={printPage}>
                  <PrintIcon className="size-4" />
                  {t('orderDetails.print')}
                </Button>
                <Button size="sm" variant="secondary" aria-expanded={editing} onClick={() => setEditing((open) => !open)}>
                  {editing ? t('orderDetails.done') : t('orderDetails.edit')}
                </Button>
              </span>
            }
          />
        }
      >
        {/* G12: who keyed it in matters to staff, not to the customer holding the ticket. */}
        {order.data.staff_id && <p className="text-muted text-sm print:hidden">{t(order.data.customer_id ? 'orderDetails.staffCreatedFor' : 'orderDetails.staffCreated')}</p>}
      </OrderCard>
      <LiveIndicator status={live} />
      {editing && <EditPanel scope={scope} order={order.data} main={context.settings.main_language} onEdited={take} onStale={stale} />}
      <History scope={scope} />
    </Page>
  )
}

/** I2: the order's audited changes, oldest first; the ticket above shows placement and the current status. */
function History({ scope }: { scope: Scope }) {
  const t = useT()
  const history = useQuery(orderHistoryQuery(scope.restaurantId, scope.branchId, scope.orderId))
  return (
    <section aria-labelledby="order-history" className="flex flex-col gap-2 print:hidden">
      <h2 id="order-history" className="text-lg font-semibold">
        {t('audit.history.title')}
      </h2>
      {history.isPending && <p className="text-muted text-sm">{t('common.loading')}</p>}
      {history.error && <FormError error={history.error} />}
      {history.data &&
        (history.data.items.length === 0 ? (
          <p className="text-muted text-sm">{t('audit.history.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {history.data.items.map((entry) => (
              <li key={entry.id}>
                <AuditEntryRow entry={entry} />
              </li>
            ))}
          </ul>
        ))}
    </section>
  )
}

interface EditProps {
  scope: Scope
  order: QueueOrder
  main: Language
  onEdited: (updated: QueueOrder) => void
  onStale: () => void
}

/** The PRD's two tabs: return items, real weight. */
function EditPanel({ scope, order, main, onEdited, onStale }: EditProps) {
  const t = useT()
  const [tab, setTab] = useState<Tab>('return')
  const tabs: { id: Tab; label: string }[] = [
    { id: 'return', label: t('orderDetails.tab.return') },
    { id: 'weight', label: t('orderDetails.tab.weight') },
  ]
  return (
    <Card className="flex flex-col gap-3 print:hidden">
      <div role="tablist" aria-label={t('orderDetails.tabs')} className="flex gap-2">
        {tabs.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            role="tab"
            aria-selected={tab === candidate.id}
            onClick={() => setTab(candidate.id)}
            className={cx(
              'min-h-9 rounded-full border px-3 text-sm font-medium select-none',
              tab === candidate.id ? 'bg-accent text-accent-fg border-accent' : 'border-border text-muted hover:bg-accent-soft',
            )}
          >
            {candidate.label}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {tab === 'return' ? (
          <ReturnTab scope={scope} order={order} main={main} onEdited={onEdited} />
        ) : (
          <WeightTab scope={scope} order={order} main={main} onEdited={onEdited} onStale={onStale} />
        )}
      </div>
    </Card>
  )
}

function ReturnTab({ scope, order, main, onEdited }: Omit<EditProps, 'onStale'>) {
  const t = useT()
  const name = useLocalizedText(main)
  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted text-sm">{t('orderDetails.returnHint')}</p>
      <ul className="divide-border divide-y">
        {order.lines.map((line) => (
          <li key={line.id} className="flex flex-col gap-2 py-3">
            <p className="font-medium">{name(line.name)}</p>
            <LineNote line={line} />
            <ReturnForm scope={scope} line={line} label={t('orderDetails.returnAmount', { name: name(line.name) })} onEdited={onEdited} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function ReturnForm({ scope, line, label, onEdited }: { scope: Scope; line: PlacedOrderLine; label: string; onEdited: (updated: QueueOrder) => void }) {
  const t = useT()
  const language = useLanguage()
  const [amount, setAmount] = useState('')
  const returning = useMutation({
    mutationFn: (amount: string) => returnLine(scope.restaurantId, scope.branchId, scope.orderId, line.id, { amount }),
    onSuccess: (updated) => {
      setAmount('')
      onEdited(updated)
    },
  })
  const remaining = remainingSteps(line)
  if (remaining === undefined) return null
  const now = t('orderDetails.now', { quantity: formatQuantity(fromSteps(remaining, line.step), language) })
  if (remaining === 0) {
    return (
      <p className="text-muted text-sm">
        {now} · {t('orderDetails.nothingLeft')}
      </p>
    )
  }
  const steps = parseSteps(amount, line.step)
  const valid = steps !== undefined && steps > 0 && steps <= remaining
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        if (valid) returning.mutate(fromSteps(steps, line.step))
      }}
    >
      <p className="text-muted text-sm">{now}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input aria-label={label} inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} className="w-28" />
        <Button type="submit" size="sm" variant="secondary" disabled={!valid} loading={returning.isPending}>
          {t('orderDetails.return')}
        </Button>
        {valid && <span className="text-sm">{t('orderDetails.after', { quantity: formatQuantity(fromSteps(remaining - steps, line.step), language) })}</span>}
      </div>
      <FormError error={returning.error} />
    </form>
  )
}

function WeightTab({ scope, order, main, onEdited, onStale }: EditProps) {
  const t = useT()
  const name = useLocalizedText(main)
  if (!ACTIVE_STATUSES.includes(order.status)) return <p className="text-muted text-sm">{t('orderDetails.weightNotActive')}</p>
  const byWeight = order.lines.filter((line) => line.step === '0.1')
  if (byWeight.length === 0) return <p className="text-muted text-sm">{t('orderDetails.noWeightItems')}</p>
  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted text-sm">{t('orderDetails.weightHint')}</p>
      <ul className="divide-border divide-y">
        {byWeight.map((line) => (
          <li key={line.id} className="flex flex-col gap-2 py-3">
            <p className="font-medium">{name(line.name)}</p>
            <LineNote line={line} />
            {/* Keyed on the quantity: a colleague's weight lands as a fresh draft, not under a stale one. */}
            <WeightForm key={line.quantity} scope={scope} line={line} label={t('orderDetails.weight', { name: name(line.name) })} onEdited={onEdited} onStale={onStale} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function WeightForm({
  scope,
  line,
  label,
  onEdited,
  onStale,
}: {
  scope: Scope
  line: PlacedOrderLine
  label: string
  onEdited: (updated: QueueOrder) => void
  onStale: () => void
}) {
  const t = useT()
  const language = useLanguage()
  const [weight, setWeight] = useState(line.quantity)
  const weighing = useMutation({
    mutationFn: (weight: string) => setRealWeight(scope.restaurantId, scope.branchId, scope.orderId, line.id, { weight }),
    onSuccess: onEdited,
    onError: (error) => {
      if (claim(error, 'ORDER_MOVED')) onStale()
    },
  })
  const steps = parseSteps(weight, line.step)
  const returned = toSteps(line.returned_quantity, line.step) ?? 0
  const valid = steps !== undefined && steps > 0 && steps >= returned && steps !== toSteps(line.quantity, line.step)
  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault()
        if (valid) weighing.mutate(fromSteps(steps, line.step))
      }}
    >
      <p className="text-muted text-sm">{t('orderDetails.now', { quantity: formatQuantity(line.quantity, language) })}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Input aria-label={label} inputMode="decimal" value={weight} onChange={(event) => setWeight(event.target.value)} className="w-28" />
        <Button type="submit" size="sm" variant="secondary" disabled={!valid} loading={weighing.isPending}>
          {t('orderDetails.saveWeight')}
        </Button>
      </div>
      <FormError error={weighing.error} />
    </form>
  )
}
